import { Injectable } from '@angular/core'
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http'
import { Observable, throwError, empty, of } from 'rxjs'
import { HttpUrlEncodingCodec } from './../services/auth/encoder'
import { Clearing } from '@models/clearing.model'
import { PossibleValue } from '@models/possible-value.model'
import { environment } from '@env/environment'
const BASE_PATH = environment.dhPath
const BASE_PATH_BFF =environment.basePath
/**
 * Service class to communicate with the REST services
 */
@Injectable({
  providedIn: 'root',
})
export class ClearingService {
  constructor(
    protected httpClient: HttpClient,
    protected encoder: HttpUrlEncodingCodec
  ) { }
  /**
   * Validate the integrity of all fields of the given com.vermeg.portal.operation.common.Clearing
   * @param {Clearing} body
   * @returns {  Observable<any>}
   */
  validate(body?: Clearing): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/clearings/validate`, body, {
      headers: headers,
    })
  }
  /**
   * Export the selected elements with the givenFormat
   * @param {string} format
   * @param {string} keys
   * @param {string} criteria
   * @param {string} searchOrder
   * @param {Clearing} body
   * @returns {  Observable<File>}
   */
  export(
    format?: string,
    keys?: string,
    criteria?: string,
    searchOrder?: string,
    body?: Clearing[]
  ): Observable<any> {
    let params = new HttpParams({ encoder: this.encoder })
    if (format !== undefined && format !== null) {
      params = params.append('format', format.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (searchOrder !== undefined && searchOrder !== null) {
      params = params.append('searchOrder', searchOrder.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post(`${BASE_PATH}/clearings/export`, body, {
      params: params,
      headers: headers,
      observe: 'response',
      responseType: 'blob',
    })
  }
  /**
   * Returns the possible values of the given field
   * @param {string} field
   * @param {string} criteria
   * @param {Clearing} body
   * @returns {  Observable<any[]>}
   */
  getPossibleValues(
    field: string,
    criteria?: string,
    body?: Clearing
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/clearings/${field}/possibleValues`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Performs a search operation without pagination
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @returns {  Observable<any[]>}
   */
  findAll(criteria?: string, order?: string, keys?: string): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (order !== undefined && order !== null) {
      params = params.append('order', order.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<any[]>(`${BASE_PATH}/clearings/search/all`, {
      params: params,
    })
  }
  /**
   * Find by the clearing identifiers
   * @param {string} keys the required keys
   * @returns {Observable<Clearing>}
   */
  findByCode(keys?: string): Observable<Clearing> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<Clearing>(`${BASE_PATH}/clearings`, {
      params: params,
    })
  }
  /**
   * Saves the clearing
   * @param {Clearing} body
   * @returns {Observable<Clearing>}
   */
  insert(body?: Clearing): Observable<Clearing> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<Clearing>(`${BASE_PATH}/clearings`, body, {
      headers: headers,
    })
  }
  /**
   * Updates the clearing
   * @param {Clearing} body
   * @returns {Observable<Clearing>}
   */
  update(body?: Clearing): Observable<Clearing> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.put<Clearing>(`${BASE_PATH}/clearings`, body, {
      headers: headers,
    })
  }
  /**
   * Deletes all clearings with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  delete(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/clearings`, {
      params: params,
    })
  }
  /**
   * Returns the possible values of the given field translated using the current user profile
   * @param {string} field
   * @param {string} keys
   * @param {string} seperator
   * @param {string} criteria
   * @param {Clearing} body
   * @returns {  Observable<any[]>}
   */
  getTranslatedPossibleValues(
    field: string,
    keys?: string,
    seperator?: string,
    criteria?: string,
    body?: Clearing
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (seperator !== undefined && seperator !== null) {
      params = params.append('seperator', seperator.toString())
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/clearings/${field}/possibleValues/translated`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Evaluates the given formula against the given clearing instance
   * @param {string} formula
   * @param {Clearing} body
   * @returns {  Observable<any>}
   */
  evaluateFormula(formula?: string, body?: Clearing): Observable<any> {
    let params = new HttpParams({ encoder: this.encoder })
    if (formula !== undefined && formula !== null) {
      params = params.append('formula', formula.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/clearings/evaluate`, body, {
      params: params,
      headers: headers,
    })
  }
  /**
   * Validate the integrity of the fieldName in the given clearing instance
   * @param {string} field
   * @param {Clearing} body
   * @returns {  Observable<any>}
   */
  validateField(field: string, body?: Clearing): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(
      `${BASE_PATH}/clearings/${field}/validate`,
      body,
      { headers: headers }
    )
  }
  /**
   * Validate the integrity of all fields of the given Clearing
   * @param {Clearing} body
   * @returns {  Observable<any>}
   */
  validateAll(body?: Clearing): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(
      `${BASE_PATH}/clearings/validate/all`,
      body,
      { headers: headers }
    )
  }
  /**
   * Find the clearing by code
   * @param {string} code
   * @param {string} keys the required keys
   * @returns {Observable<Clearing>}
   */
  findByCodeAsString(code: string, keys?: string): Observable<Clearing> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<Clearing>(`${BASE_PATH}/clearings/${code}`, {
      params: params,
    })
  }
  /**
   * Deletes the clearing instance with the given code
   * @param {string} code
   * @returns {  Observable<any>}
   */
  deleteOne(code: string): Observable<any> {
    return this.httpClient.delete<any>(`${BASE_PATH}/clearings/${code}`)
  }
  /**
   * Deletes all clearings with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  deleteWithCriteria(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/clearings/delete`, {
      params: params,
    })
  }
  /**
   * Counts all the clearings with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  count(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    return of(14);
  }
  /**
   * Return a new clearing instance initialized from the server
   * @returns {Observable<Clearing>}
   */
  newInstance(): Observable<Clearing> {
    return this.httpClient.get<Clearing>(`${BASE_PATH}/clearings/new`)
  }
  /**
   * Performs a paginated search on clearings
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @param {number} page the page number
   * @param {number} size the page size
   * @returns {  Observable<any[]>}
   */
  find(
    criteria?: string,
    order?: string,
    keys?: string,
    page?: number,
    size?: number
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (order !== undefined && order !== null) {
      params = params.append('order', order.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (page !== undefined && page !== null) {
      params = params.append('page', page.toString())
    }
    if (size !== undefined && size !== null) {
      params = params.append('size', size.toString())
    }
    return this.httpClient.get<any[]>(`${BASE_PATH_BFF}/bff/externaldata/proxy/clearing/all`, {
      params: params,
    })
  }
}
